import React from 'react';

import { COUNTRY, REQ_SOURCE, cleanObject, countryEnumBase, fakePromise, isDefined, isEmpty, silenceError } from '@nfctron/nexus-core';
import { UiButton, UiDrawer, UiDrawerContent, UiDrawerFooter, UiDrawerHeader, UiDrawerTitle, cn, useFormat } from '@nfctron/nexus-react';

import { Collapse, Loader, Text, Tooltip } from '@mantine/core';
import { ShieldCheckIcon, TriangleAlertIcon } from 'lucide-react';
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form';
import validator from 'validator';
import isEmail from 'validator/lib/isEmail';
import isMobilePhone from 'validator/lib/isMobilePhone';
import isPostalCode from 'validator/lib/isPostalCode';

import packageJson from '@root/config/package.json';

import { ApiError } from '@root/features/api-client/api-client';
import { useApiClient } from '@root/features/api-client/api-client.context';
import { type AresResponse } from '@root/features/api-client/generated/pass.api';
import { useSessionStore } from '@root/features/authentication/stores/sessionStore';
import { Form } from '@root/features/form/components/Form/Form';
import { getPasswordStrength } from '@root/features/form/components/PasswordInput/PasswordInput';

import { Trans } from '@root/lib/components/Trans/Trans';
import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { countryEnumTickets } from '@root/lib/constants/country';
import { useHandler } from '@root/lib/hooks/useHandler';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type Component } from '@root/lib/types/components';

import { type RegisterSheetFormValues, type RegisterSheetProps } from './RegisterSheet.types';

/**
 * RegisterSheet component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const RegisterSheet: Component<RegisterSheetProps> = ({ open, onClose, formProps, onSubmit, type = 'register', children, ...props }) => {
	const session = useSessionStore((state) => state.account);
	const { t } = useTranslate(APP_I18N_NAMESPACE.COMMON, RegisterSheet.displayName);
	const { t: typeSensitiveT } = useTranslate(APP_I18N_NAMESPACE.COMMON, `${RegisterSheet.displayName}.${type}`);
	const { t: baseT } = useTranslate(APP_I18N_NAMESPACE.COMMON);
	const { passApi } = useApiClient();
	const format = useFormat();
	const form = useForm<RegisterSheetFormValues>({
		mode: 'all',
		criteriaMode: 'firstError',
		defaultValues: {
			firstName: '',
			lastName: '',
			email: '',
			phone: '',
			languageId: format.language.languageId,
			withBillingInfo: false,
			termsAndConditions: false,
			password: '',
			billingInfo: {
				address: '',
				city: '',
				postalCode: '',
				legalName: '',
				vatId: '',
				taxId: '',
				regNumber: '',
				countryId: undefined,
			},
		},
		...formProps,
	});

	/** register submit handler */
	const { handler: onSubmitHandler, isProcessing: isSubmitting } = useHandler<SubmitHandler<RegisterSheetFormValues>>(
		async (values) => {
			/** force assign language if not present (may not be registered) */
			values.languageId = values.languageId ?? format.language.languageId;
			/** remove password field if it isn't the base register form */
			if (type === 'cart') values.password = '';
			/** clean up the values */
			const cleanValues = cleanObject(values, { trimString: true });
			/** normalize email address */
			cleanValues.email = validator.normalizeEmail(cleanValues.email, { gmail_remove_dots: false }) || cleanValues.email;
			/** call external `onSubmit` callback */
			if (isDefined(onSubmit)) return onSubmit(cleanValues);

			/** call register API */
			passApi.account.register
				.request({
					firstName: cleanValues.firstName,
					lastName: cleanValues.lastName,
					email: cleanValues.email,
					password: cleanValues.password,
					phone: cleanValues.phone ?? undefined,
					address: cleanValues.billingInfo.address ?? undefined,
					city: cleanValues.billingInfo.city ?? undefined,
					postalCode: cleanValues.billingInfo.postalCode ?? undefined,
					legalName: cleanValues.billingInfo.legalName ?? undefined,
					vatId: cleanValues.billingInfo.vatId ?? undefined,
					regNumber: cleanValues.billingInfo.regNumber ?? undefined,
					languageId: cleanValues.languageId,
					reqSource: REQ_SOURCE.WEB,
					reqVersion: packageJson.version,
				})
				.then((registerRes) => {
					/** close if ok */
					if (registerRes.data.ok) return onClose?.();
					throw new ApiError(registerRes.data.error ?? 'register_error', registerRes.status);
				})
				.catch((e: ApiError) => {
					/** taken email error */
					if (e.name === 'email_already_exists') {
						form.setError('email', {
							type: 'manual',
							message: t('form.email.errors.taken.text'),
						});
					}
					// TODO: handle other errors
				});
		},
		{ deps: [onSubmit] },
	);

	/** watch `withBillingInfo` field as it is conditionally used to render other fields */
	const isWithBillingInfo = useWatch({
		control: form.control,
		name: 'withBillingInfo',
	});

	/** watch `regNumber` value to fetch from ares API */
	const regNumberValue = useWatch({
		control: form.control,
		name: 'billingInfo.regNumber',
	});

	/** watch `countryId` value to fetch from ares API */
	const countryIdValue = useWatch({
		control: form.control,
		name: 'billingInfo.countryId',
	});
	const countryEnumerated = isDefined(countryIdValue) ? countryEnumBase.get(countryIdValue) : null;

	/** ares API handler */
	const {
		handler: aresHandler,
		isProcessing: aresProcessing,
		error: aresError,
		cleanUp,
	} = useHandler<(regNumber: string) => Promise<AresResponse>>(async (regNumber) => {
		const { data } = await passApi.pass.ares.request({ ico: regNumber });
		/** fake a delay since it's too fast, lol #sufferingfromsuccess */
		await fakePromise(500);
		return data;
	});

	/** listen for valid `regNumber` field to call ares API for legal info */
	React.useEffect(() => {
		if (countryIdValue === COUNTRY.CZE && regNumberValue?.match(/^\d{8}$/)) {
			aresHandler(regNumberValue).then((aresRes) => {
				if (aresRes instanceof ApiError) return;
				/** set values from ARES response */
				form.setValue('withBillingInfo', true);
				form.setValue('billingInfo.legalName', aresRes.legalName, {
					shouldValidate: true,
				});
				form.setValue('billingInfo.vatId', aresRes.vatId, {
					shouldValidate: true,
				});
				form.setValue('billingInfo.address', (aresRes.address1 ?? '').concat(isEmpty(aresRes.address2) ? `, ${aresRes.address2}` : ''), {
					shouldValidate: true,
				});
				form.setValue('billingInfo.city', aresRes.city, {
					shouldValidate: true,
				});
				form.setValue('billingInfo.postalCode', aresRes.postalCode, {
					shouldValidate: true,
				});
				form.setValue('billingInfo.countryId', COUNTRY.CZE, {
					shouldValidate: true,
				});
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [regNumberValue, countryIdValue]);

	/** callback handler for closing sheet */
	const onCloseSheet = React.useCallback(() => {
		form.reset();
		cleanUp();
		onClose?.();
	}, [cleanUp, form, onClose]);

	/** reset form upon formProps change, to ensure up-to-date initialValues */
	React.useEffect(() => {
		if (isDefined(formProps) && isDefined(formProps.defaultValues)) {
			form.resetField('firstName', {
				defaultValue: formProps.defaultValues.firstName,
			});
			form.resetField('lastName', {
				defaultValue: formProps.defaultValues.lastName,
			});
			form.resetField('email', { defaultValue: formProps.defaultValues.email });
			form.resetField('phone', { defaultValue: formProps.defaultValues.phone });
			form.resetField('languageId', {
				defaultValue: formProps.defaultValues.languageId,
			});
			form.resetField('withBillingInfo', {
				defaultValue: formProps.defaultValues.withBillingInfo,
			});
			form.resetField('termsAndConditions', {
				defaultValue: formProps.defaultValues.termsAndConditions,
			});
			form.resetField('password', {
				defaultValue: formProps.defaultValues.password,
			});
			form.resetField('billingInfo.legalName', {
				defaultValue: formProps.defaultValues.billingInfo?.legalName ?? '',
			});
			form.resetField('billingInfo.vatId', {
				defaultValue: formProps.defaultValues.billingInfo?.vatId ?? '',
			});
			form.resetField('billingInfo.taxId', {
				defaultValue: formProps.defaultValues.billingInfo?.taxId ?? '',
			});
			form.resetField('billingInfo.regNumber', {
				defaultValue: formProps.defaultValues.billingInfo?.regNumber ?? '',
			});
			form.resetField('billingInfo.address', {
				defaultValue: formProps.defaultValues.billingInfo?.address ?? '',
			});
			form.resetField('billingInfo.city', {
				defaultValue: formProps.defaultValues.billingInfo?.city ?? '',
			});
			form.resetField('billingInfo.postalCode', {
				defaultValue: formProps.defaultValues.billingInfo?.postalCode ?? '',
			});
			form.resetField('billingInfo.countryId', {
				defaultValue: formProps.defaultValues.billingInfo?.countryId ?? undefined,
			});
		}
	}, [formProps?.defaultValues]);

	return (
		<UiDrawer open={open} onClose={onClose} handleOnly={isDefined(onClose) ? false : true} repositionInputs={false}>
			<UiDrawerContent className="mx-auto max-w-screen-sm">
				<UiDrawerHeader className="flex justify-center border-b border-zinc-200 py-6">
					<UiDrawerTitle asChild>
						<Text fw={700} c="lucky-point" fz={18}>
							{typeSensitiveT('title')}
						</Text>
					</UiDrawerTitle>
				</UiDrawerHeader>

				<div className="flex max-h-full flex-col gap-3 overflow-scroll p-4">
					{/* children */}
					{children}

					{/* register form */}
					<Form
						className={cn('flex flex-col gap-3', isSubmitting && 'pointer-events-none opacity-50')}
						onSubmit={onSubmitHandler}
						form={form}
						withInvisibleSubmit
					>
						{/* fullName */}
						<div className="grid grid-cols-2 gap-3">
							{/* firstName input */}
							<Form.TextInput<RegisterSheetFormValues>
								fieldId="firstName"
								label={t('form.firstName.label')}
								autoComplete="given-name"
								options={{ required: t('form.firstName.errors.required.text') }}
								placeholder={t('form.firstName.placeholder')}
							/>

							{/* lastName input */}
							<Form.TextInput<RegisterSheetFormValues>
								fieldId="lastName"
								label={t('form.lastName.label')}
								autoComplete="family-name"
								options={{ required: t('form.lastName.errors.required.text') }}
								placeholder={t('form.lastName.placeholder')}
							/>
						</div>

						{/* email input */}
						<Form.TextInput<RegisterSheetFormValues>
							fieldId="email"
							label={t('form.email.label')}
							radius="md"
							autoComplete="email"
							type="email"
							options={{
								required: t('form.email.errors.required.text'),
								validate: (email) => {
									if (!isEmpty(email) && !isEmail(email as string)) return t('form.email.errors.invalid.text');
								},
							}}
							placeholder={t('form.email.placeholder')}
						/>

						{/* phone input */}
						<Form.TextInput<RegisterSheetFormValues>
							fieldId="phone"
							label={t('form.phone.label')}
							type="tel"
							autoComplete="tel"
							options={{
								validate: (phone) => {
									if (!isEmpty(phone) && !isMobilePhone(phone as string)) return t('form.phone.errors.invalid.text');
								},
							}}
							placeholder={t('form.phone.placeholder')}
						/>

						{/* password input */}
						{type === 'base' && (
							<Form.PasswordInput<RegisterSheetFormValues>
								withRequirements
								fieldId="password"
								label={t('form.password.label')}
								autoComplete="new-password"
								options={{
									required: t('form.password.errors.required.text'),
									validate: (password) => {
										const strength = getPasswordStrength(password as string);
										if (strength < 100) return t('form.password.errors.weak.text');
									},
								}}
							/>
						)}

						{/* fill billing checkbox */}
						<Form.Checkbox<RegisterSheetFormValues> fieldId="withBillingInfo" label={typeSensitiveT('form.withBillingInfo.label')} />

						{/* billing info fields */}
						<Collapse in={isWithBillingInfo}>
							<div className="flex flex-col gap-3">
								{/* country input */}
								<Form.Select<RegisterSheetFormValues>
									clearable
									fieldId="billingInfo.countryId"
									label={t('form.billingInfo.countryId.label')}
									options={{
										required: isWithBillingInfo ? t('form.billingInfo.countryId.errors.required.text') : false,
									}}
									disabled={aresProcessing}
									autoComplete="country"
									data={countryEnumTickets.list
										.filter((country) => country.systemWideSupport)
										.map((country) => ({
											value: country.countryId.toString(),
											label: baseT(`constants.country.${country.countryId}.text`),
											data: country,
										}))}
									transform={{
										input: (v) => (v as COUNTRY)?.toString(),
										output: (v) => silenceError(() => countryEnumTickets.get(parseInt(v || '', 10)).countryId, null),
									}}
									placeholder={t('form.billingInfo.countryId.placeholder')}
								/>

								<Collapse in={isDefined(countryIdValue)}>
									{/* regNumber/vatId */}
									<div className="grid grid-cols-2 gap-3">
										{/* regNumber input */}
										<Form.TextInput<RegisterSheetFormValues>
											fieldId="billingInfo.regNumber"
											label={t('form.billingInfo.regNumber.label')}
											placeholder={t('form.billingInfo.regNumber.placeholder')}
											options={{
												validate: (regNumber) => {
													const regNumberString = !isEmpty(regNumber) ? String(regNumber) : null;
													if (!isEmpty(regNumberString)) {
														/** validate length */
														if (regNumberString.length > 25) return t('form.billingInfo.regNumber.errors.invalid.text');
														/** validate numeric */
														if (!/^\d+$/.test(regNumberString)) return t('form.billingInfo.regNumber.errors.invalid.text');
														/** validate country-specific */
														if (countryIdValue === COUNTRY.CZE && !/^\d{8}$/.test(regNumberString))
															return t('form.billingInfo.regNumber.errors.invalid.text');
													}
												},
											}}
											rightSection={
												aresProcessing ? (
													<Loader size="xs" variant="oval" />
												) : isDefined(aresError) ? (
													<Tooltip color="orange" label={t('form.billingInfo.regNumber.errors.notFound.text')}>
														<span>
															<TriangleAlertIcon className="text-lg text-amber-500" />
														</span>
													</Tooltip>
												) : countryIdValue === COUNTRY.CZE ? (
													<ShieldCheckIcon className="text-zinc-900" size={16} />
												) : null
											}
											disabled={aresProcessing}
										/>

										{/* vatId input */}
										<Form.TextInput<RegisterSheetFormValues>
											fieldId="billingInfo.vatId"
											label={t('form.billingInfo.vatId.label', { context: countryEnumerated?.isoChar3 })}
											placeholder={t('form.billingInfo.vatId.placeholder', { context: countryEnumerated?.isoChar3 })}
											disabled={aresProcessing}
											options={{
												validate: (vatId) => {
													const vatIdString = !isEmpty(vatId) ? String(vatId) : null;
													if (!isEmpty(vatIdString) && isDefined(countryEnumerated)) {
														/** validate country-specific */
														if (!countryEnumerated.vatIdRegEx?.test(vatIdString)) return t('form.billingInfo.vatId.errors.invalid.text');
													}
												},
											}}
										/>
									</div>

									{/* taxId input (only for SVK) */}
									{countryEnumerated?.countryId === COUNTRY.SVK && (
										<Form.TextInput<RegisterSheetFormValues>
											fieldId="billingInfo.taxId"
											label={t('form.billingInfo.taxId.label', { context: countryEnumerated?.isoChar3 })}
											placeholder={t('form.billingInfo.taxId.placeholder', { context: countryEnumerated?.isoChar3 })}
											disabled={aresProcessing}
											options={{
												validate: (taxId) => {
													const taxIdString = !isEmpty(taxId) ? String(taxId) : null;
													if (!isEmpty(taxIdString) && isDefined(countryEnumerated)) {
														/** validate country-specific */
														if (!countryEnumerated.taxIdRegEx?.test(taxIdString)) return t('form.billingInfo.taxId.errors.invalid.text');
													}
												},
											}}
										/>
									)}

									{/* legalName input */}
									<Form.TextInput<RegisterSheetFormValues>
										fieldId="billingInfo.legalName"
										label={t('form.billingInfo.legalName.label')}
										autoComplete="organization"
										disabled={aresProcessing}
										placeholder={t('form.billingInfo.legalName.placeholder')}
									/>

									{/* address input */}
									<Form.TextInput<RegisterSheetFormValues>
										fieldId="billingInfo.address"
										label={t('form.billingInfo.address.label')}
										autoComplete="address-line1"
										options={{
											required: isWithBillingInfo ? t('form.billingInfo.address.errors.required.text') : false,
										}}
										disabled={aresProcessing}
										placeholder={t('form.billingInfo.address.placeholder')}
									/>

									{/* city/postalCode */}
									<div className="grid grid-cols-2 gap-3">
										{/* city input */}
										<Form.TextInput<RegisterSheetFormValues>
											fieldId="billingInfo.city"
											label={t('form.billingInfo.city.label')}
											autoComplete="address-level2"
											options={{
												required: isWithBillingInfo ? t('form.billingInfo.city.errors.required.text') : false,
											}}
											disabled={aresProcessing}
											placeholder={t('form.billingInfo.city.placeholder')}
										/>

										{/* postalCode input */}
										<Form.TextInput<RegisterSheetFormValues>
											fieldId="billingInfo.postalCode"
											label={t('form.billingInfo.postalCode.label')}
											autoComplete="postal-code"
											options={{
												required: isWithBillingInfo ? t('form.billingInfo.postalCode.errors.required.text') : false,
												validate: (postalCode) => {
													if (!isEmpty(postalCode) && !isPostalCode(postalCode as string, 'any'))
														return t('form.billingInfo.postalCode.errors.invalid.text');
												},
											}}
											disabled={aresProcessing}
											placeholder={t('form.billingInfo.postalCode.placeholder')}
										/>
									</div>
								</Collapse>
							</div>
						</Collapse>

						{/* terms and conditions */}
						{!isDefined(session) && (
							<Form.Checkbox<RegisterSheetFormValues>
								fieldId="termsAndConditions"
								options={{ required: true }}
								label={<Trans tFunc={t} i18nKey="form.termsAndConditions.text" />}
							/>
						)}
					</Form>
				</div>

				<UiDrawerFooter>
					<UiButton
						type="submit"
						size="lg"
						onClick={form.handleSubmit(onSubmitHandler)}
						variant="primary"
						loading={isSubmitting}
						fullWidth
						disabled={aresProcessing}
					>
						{typeSensitiveT('submitButton.text')}
					</UiButton>
				</UiDrawerFooter>
			</UiDrawerContent>
		</UiDrawer>
	);
};

/**
 * RegisterSheet component default props
 * @type {ComponentDisplayName}
 */
RegisterSheet.displayName = 'component/RegisterSheet';
